/** Session scheduling: the Schedule tab, the cross-campaign "Next sessions" list, and the end-of-session prompt. */
import { useEffect, useMemo, useState } from "react";
import {
  describeRule, localDate, zonedParts, WEEKDAY_NAMES,
  type ScheduleOccurrence, type ScheduleResponse, type ScheduleRuleInput, type UpcomingResponse,
} from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";

const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** "Wed, Oct 28, 8:00 PM EDT" in the viewer's own time zone. */
export const whenLocal = (iso: string) =>
  new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(new Date(iso));

/** "in 2d 3h", "in 45 min", "now". */
export function countdown(iso: string, durationMin: number, now: number): string {
  const ms = Date.parse(iso) - now;
  if (ms <= 0) return now < Date.parse(iso) + durationMin * 60_000 ? "happening now" : "over";
  const min = Math.round(ms / 60_000);
  if (min < 60) return `in ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `in ${h}h ${min % 60}m`;
  return `in ${Math.floor(h / 24)}d ${h % 24}h`;
}

function useNow(ms = 30_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

const tzList = (): string[] => {
  const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
  return f ? f("timeZone") : ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London", "UTC"];
};

/** The Schedule tab (everyone reads; Hosts and Co-hosts edit). */
export function SchedulePage() {
  const { campaign, scheduleVersion, fail } = useGame();
  const [data, setData] = useState<ScheduleResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const now = useNow();
  useEffect(() => void api.schedule(campaign.id).then(setData, fail), [campaign.id, scheduleVersion, fail]);
  const run = async (fn: () => Promise<ScheduleResponse>) => {
    setBusy(true);
    try {
      setData(await fn());
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  if (!data) return <main className="muted">Loading…</main>;
  const tzNote = data.timezone === browserTz ? "" : ` Times below are in your time zone (${browserTz}); the schedule itself runs on ${data.timezone}.`;
  return (
    <main className="schedule-page">
      <section className="panel">
        <h3>Upcoming sessions</h3>
        <p className="muted small">{tzNote.trim() || `Times are in your time zone (${browserTz}).`}</p>
        {!data.upcoming.length && <p className="muted">Nothing scheduled yet.{data.canEdit ? " Add a session below, or tell the DM: \"Dungeon Master, schedule next session for Wednesday at 8.\"" : " The Host sets the schedule."}</p>}
        <ul className="schedule-list">
          {data.upcoming.map((o) => (
            <Occurrence key={`${o.ruleId}-${o.date}`} o={o} canEdit={data.canEdit} busy={busy} now={now}
              onSkip={(undo) => run(() => api.skipSession(campaign.id, o.ruleId, o.date, undo))}
              onMove={(iso) => run(() => api.moveSession(campaign.id, o.ruleId, o.date, iso))} />
          ))}
        </ul>
      </section>
      {data.canEdit && <AddRule timezone={data.timezone} busy={busy} onAdd={(r) => run(() => api.addScheduleRule(campaign.id, r))} />}
      {data.rules.length > 0 && (
        <section className="panel">
          <h3>Schedule</h3>
          <ul className="schedule-rules">
            {data.rules.map((r) => (
              <li key={r.id}>
                <strong>{describeRule(r)}</strong> <span className="muted small">({data.timezone}, {Math.round(r.durationMin / 6) / 10} h{r.createdByName ? `, set by ${r.createdByName}` : ""})</span>
                {r.note && <span className="small"> · {r.note}</span>}
                {data.canEdit && (
                  <button className="link danger" disabled={busy}
                    onClick={() => window.confirm(`Delete "${describeRule(r)}"? All its sessions disappear from the schedule.`) && void run(() => api.deleteScheduleRule(campaign.id, r.id))}>Delete</button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {data.canEdit && (
        <section className="panel">
          <h3>Schedule settings</h3>
          <div className="row">
            <label>Time zone{" "}
              <select value={data.timezone} disabled={busy} onChange={(e) => void run(() => api.scheduleSettings(campaign.id, { timezone: e.target.value }))}>
                {[...new Set([data.timezone, ...tzList()])].map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </label>
          </div>
          <div className="row">
            <label><input type="checkbox" checked={data.announce} disabled={busy} onChange={(e) => void run(() => api.scheduleSettings(campaign.id, { announce: e.target.checked }))} /> Announce schedule changes in Discord</label>
            <label><input type="checkbox" checked={data.remind24h} disabled={busy} onChange={(e) => void run(() => api.scheduleSettings(campaign.id, { remind24h: e.target.checked }))} /> Reminder 24 hours before</label>
            <label><input type="checkbox" checked={data.remind1h} disabled={busy} onChange={(e) => void run(() => api.scheduleSettings(campaign.id, { remind1h: e.target.checked }))} /> Reminder 1 hour before</label>
          </div>
          <p className="muted small">
            {data.channelName
              ? `Discord posts go to #${data.channelName}'s text chat (or the server's system channel if the bot can't post there).`
              : "No Discord channel linked yet: link a voice channel in Campaign settings (or /join) to get Discord announcements and reminders."}
            {" "}Times stay put across daylight-saving changes (8 PM stays 8 PM in {data.timezone}).
          </p>
        </section>
      )}
    </main>
  );
}

function Occurrence({ o, canEdit, busy, now, onSkip, onMove }: {
  o: ScheduleOccurrence; canEdit: boolean; busy: boolean; now: number; onSkip: (undo: boolean) => void; onMove: (iso: string) => void;
}) {
  const { campaign } = useGame();
  const [moving, setMoving] = useState(false);
  const [to, setTo] = useState(() => toInput(o.at));
  return (
    <li className={o.cancelled ? "cancelled" : ""}>
      <div>
        <strong>{whenLocal(o.at)}</strong>
        {!o.cancelled && <span className="muted small"> · {countdown(o.at, o.durationMin, now)}</span>}
        {o.recurring && <span className="tag">repeats</span>}
        {o.moved && <span className="tag">moved</span>}
        {o.cancelled && <span className="tag danger">cancelled</span>}
        {o.note && <span className="small"> · {o.note}</span>}
      </div>
      <div className="row small">
        {!o.cancelled && <a href={api.sessionIcsUrl(campaign.id, o.ruleId, o.date)} download>Add to calendar (.ics)</a>}
        {canEdit && (o.cancelled || o.moved
          ? <button className="link" disabled={busy} onClick={() => onSkip(true)}>{o.cancelled ? "Restore" : "Undo move"}</button>
          : <button className="link danger" disabled={busy} onClick={() => window.confirm("Cancel this one session? (The rest of the schedule stays.)") && onSkip(false)}>Cancel this one</button>)}
        {canEdit && !o.cancelled && <button className="link" disabled={busy} onClick={() => setMoving(!moving)}>Reschedule</button>}
        {moving && (
          <>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} aria-label="New date and time (your time zone)" />
            <button disabled={busy || !to} onClick={() => { onMove(new Date(to).toISOString()); setMoving(false); }}>Move (your time)</button>
          </>
        )}
      </div>
    </li>
  );
}

/** datetime-local value in the browser's zone. */
const toInput = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function AddRule({ timezone, busy, onAdd }: { timezone: string; busy: boolean; onAdd: (r: ScheduleRuleInput) => Promise<unknown> }) {
  const today = localDate(Date.now(), timezone);
  const [kind, setKind] = useState<"weekly" | "once">("weekly");
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("20:00");
  const [days, setDays] = useState<number[]>([zonedParts(Date.now(), timezone).weekday]);
  const [interval, setInterval_] = useState(1);
  const [end, setEnd] = useState("");
  const [hours, setHours] = useState(3);
  const [note, setNote] = useState("");
  const preview = useMemo(() => describeRule({ kind, startDate: date, time, weekdays: days, intervalWeeks: interval, endDate: end || null }), [kind, date, time, days, interval, end]);
  return (
    <section className="panel">
      <h3>Add sessions</h3>
      <div className="row">
        <label><input type="radio" checked={kind === "weekly"} onChange={() => setKind("weekly")} /> Repeating</label>
        <label><input type="radio" checked={kind === "once"} onChange={() => setKind("once")} /> One-off</label>
      </div>
      <div className="row">
        <label>{kind === "once" ? "Date" : "Starting"} <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label>Time <input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></label>
        <span className="muted small">({timezone})</span>
        <label>Length{" "}
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {[1, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6].map((h) => <option key={h} value={h}>{h} h</option>)}
          </select>
        </label>
      </div>
      {kind === "weekly" && (
        <>
          <div className="row weekdays">
            {WEEKDAY_NAMES.map((n, i) => (
              <label key={n}><input type="checkbox" checked={days.includes(i)} onChange={(e) => setDays(e.target.checked ? [...days, i].sort() : days.filter((d) => d !== i))} /> {n.slice(0, 3)}</label>
            ))}
          </div>
          <div className="row">
            <label>Every{" "}
              <select value={interval} onChange={(e) => setInterval_(Number(e.target.value))}>
                <option value={1}>week</option><option value={2}>other week</option><option value={3}>3 weeks</option><option value={4}>4 weeks</option>
              </select>
            </label>
            <label>Until (optional) <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
          </div>
        </>
      )}
      <div className="row">
        <input placeholder="Note (optional), e.g. Bring snacks" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
        <button className="primary" disabled={busy || (kind === "weekly" && !days.length)} onClick={() =>
          void onAdd({ kind, startDate: date, time, weekdays: days, intervalWeeks: interval, endDate: end || null, durationMin: Math.round(hours * 60), note })
            .then(() => setNote(""))}>Add</button>
      </div>
      <p className="muted small">{preview} ({timezone}). Posted in Discord if announcements are on.</p>
    </section>
  );
}

/** Campaigns page: my next session in each campaign, soonest first, with the private calendar feed. */
export function NextSessions() {
  const [data, setData] = useState<UpcomingResponse | null>(null);
  const [showFeed, setShowFeed] = useState(false);
  const now = useNow();
  useEffect(() => void api.upcoming().then(setData, () => undefined), []);
  if (!data) return null;
  return (
    <section className="panel next-sessions">
      <h3>Next sessions</h3>
      {!data.sessions.length && <p className="muted small">None of your campaigns has a session scheduled. Hosts set them on the Schedule tab.</p>}
      <ul className="schedule-list">
        {data.sessions.map((s) => (
          <li key={s.campaignId}>
            <strong>{s.campaignName}</strong> · {whenLocal(s.occurrence.at)} <span className="muted small">· {countdown(s.occurrence.at, s.occurrence.durationMin, now)}</span>
            {s.occurrence.recurring && <span className="tag">repeats</span>}
            {" "}<a className="small" href={api.sessionIcsUrl(s.campaignId, s.occurrence.ruleId, s.occurrence.date)} download>.ics</a>
          </li>
        ))}
      </ul>
      <p className="small">
        <button className="link" onClick={() => setShowFeed(!showFeed)}>Subscribe in your calendar app</button>
      </p>
      {showFeed && (
        <div className="small">
          <p className="muted">Paste this private link into Google Calendar (Other calendars → From URL), Apple Calendar or Outlook. It lists every campaign you're in and keeps itself up to date. Anyone with the link can see your schedule; reset it if it leaks.</p>
          <input readOnly value={data.feedUrl} onFocus={(e) => e.target.select()} style={{ width: "100%" }} aria-label="Calendar feed link" />
          <div className="row">
            <button onClick={() => void navigator.clipboard?.writeText(data.feedUrl)}>Copy</button>
            <button className="link danger" onClick={() => window.confirm("Make a new link? The old one stops working.") && void api.resetCalendar().then(setData)}>Reset link</button>
          </div>
        </div>
      )}
    </section>
  );
}

/** End-of-session popup: the next session, or (Hosts) a one-click "same time next week". */
export function NextSessionNote({ startedAt }: { startedAt: string | null }) {
  const { campaign, isHost, fail } = useGame();
  const [data, setData] = useState<ScheduleResponse | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => void api.schedule(campaign.id).then(setData, () => undefined), [campaign.id]);
  if (!data) return null;
  const next = data.upcoming.find((o) => !o.cancelled && Date.parse(o.at) > Date.now());
  if (next) return <p>Next session: <strong>{whenLocal(next.at)}</strong>{next.recurring ? " (repeats)" : ""}. <a href="#/schedule">Schedule</a></p>;
  if (!isHost) return <p className="muted small">No next session scheduled yet.</p>;
  // Same local time next week, rounded to the quarter hour.
  const start = startedAt ? Date.parse(startedAt) : Date.now();
  const p = zonedParts(start, data.timezone);
  const mm = Math.round(p.mm / 15) * 15;
  const hh = (p.hh + Math.floor(mm / 60)) % 24;
  const time = `${String(hh).padStart(2, "0")}:${String(mm % 60).padStart(2, "0")}`;
  const date = localDate(start + 7 * 86_400_000, data.timezone);
  const add = async (weekly: boolean) => {
    setBusy(true);
    try {
      setData(await api.addScheduleRule(campaign.id, { kind: weekly ? "weekly" : "once", startDate: date, time, weekdays: [p.weekday], intervalWeeks: 1 }));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="next-session-prompt">
      <p>No next session yet. Same time next week ({WEEKDAY_NAMES[p.weekday]} {date} at {time}, {data.timezone})?</p>
      <div className="row">
        <button disabled={busy} onClick={() => void add(false)}>Schedule it</button>
        <button disabled={busy} onClick={() => void add(true)}>Every week at this time</button>
        <a href="#/schedule">Other time…</a>
      </div>
    </div>
  );
}
